import React, { useEffect, useState } from 'react';
import {
    Input,
    Form,
    Select,
    notification,
    Steps,
    Upload,
    DatePicker,
    Row,
    Col,
    Tooltip,
    Checkbox,
    Typography,
    Spin,
} from 'antd';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useSignUpDriverMutation } from '../../../GraphQL/hooks/useSignUpDriverMutation';
import {
    AllCarModels,
    allColorOptions,
    DocumentTypeEnums,
    ProcessedDocument,
    TaxiDriverInput,
} from '../../../shared/types';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import Urls from '../../../Urls';
import { capitalizeWords, extractIdCardData } from '../../../shared/Utils';
import ImageUploadPreview from '../../../shared/Components/ImageUploadPreview';
import { useProcessIDCardMutation } from '../../../GraphQL/hooks/useProcessIDCardMutation';
import PhoneInput from 'antd-phone-input';

// refernce meta pixel id : 861885046902960
declare global {
    interface Window {
        fbq: (...args: any[]) => void;
    }
}

export const InscriptionTaxiDriver = () => {
    const [form] = Form.useForm();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [driverData, setDriverData] = useState<TaxiDriverInput>();
    const [recaptchaState, setRecaptchaState] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState<Record<string, any>>({
        driverPhoto: null,
        idCardFront: null,
        idCardBack: null,
        driverLicence: null,
        grayCardFront: null,
        grayCardBack: null,
        professionalCard: null,
        insurance: null,
    });

    const { mutation: signUpDriverMutation, loading: signUpDriverLoading } = useSignUpDriverMutation();
    const { mutation: processIDCardMutation, loading: processIDCardLoading } = useProcessIDCardMutation();

    useEffect(() => {
        setLoading(signUpDriverLoading && processIDCardLoading);
    }, [signUpDriverLoading, processIDCardLoading]);

    useEffect(() => {
        if (window.fbq) {
            window.fbq('track', 'ViewContent', {
                content_name: 'Driver Signup Form',
            });
        }
    }, []);

    useEffect(() => {
        if (i18n.language !== 'fr_FR') {
            i18n.changeLanguage('fr_FR');
        }
    }, [i18n]);

    const steps = [
        {
            title: '',
            content: t('titlePersonalInfo'),
            description1: t('textPersonalInfo'),
        },
        {
            title: '',
            content: t('titlePersonalIdentity'),
            description1: t('textPersonalIdentity1'),
            description2: t('textPersonalIdentity2'),
        },
        {
            title: '',
            content: t('titleProfessionalDocuments'),
            description1: t('textProfessionalDetails1'),
            description2: t('textProfessionalDetails2'),
        },
        /* 
        {
            title: 'Payment Details',
            content: 'We need your payment details to pay you.',
            description: 'We need your payment details to pay you.',
        }
        */
    ];

    const attachmentPersonalRequested = [
        { label: t('cinCard1'), name: 'idCardFront' },
        { label: t('cinCard2'), name: 'idCardBack' },
        { label: t('driverLicense'), name: 'driverLicence' },
    ];

    const attachmentProfessionalRequested = [
        { label: t('grayCard1'), name: 'grayCardFront' },
        { label: t('grayCard2'), name: 'grayCardBack' },
        { label: t('professionalCard'), name: 'professionalCard' },
        { label: t('insurance'), name: 'insurance' },
    ];

    const attachmentOrder = [
        'driverPhoto',
        'idCardFront',
        'idCardBack',
        'driverLicence',
        'grayCardFront',
        'grayCardBack',
        'professionalCard',
        'insurance',
    ];

    const updateAttachment = (key: string, file: any) => {
        setAttachments((prevAttachments) => ({
            ...prevAttachments,
            [key]: file,
        }));
    };

    const handleDelete = (key: string) => {
        setAttachments((prev) => ({ ...prev, [key]: null }));
    };

    const validateFormData = (data: TaxiDriverInput, attachments: Record<string, File | null>) => {
        const errors: string[] = [];

        if (!data?.fullName) errors.push(t('textErorFullname2'));
        if (!data?.email) errors.push(t('textErorEmail'));
        if (!data?.phoneNumber) errors.push(t('textErorPhone2'));
        if (!data?.birthday) errors.push(t('textErorBirthday'));
        if (!data?.marque) errors.push(t('textErorBrandVehicle'));
        if (!data?.model) errors.push(t('textErorModelVehicle2'));
        if (!data?.color) errors.push(t('textErorColorVehicle'));
        if (!data?.year) errors.push(t('textErorYearVehicle'));
        if (!data?.immatriculation) errors.push(t('textErorLicencePlate1'));
        if (!data?.licenseNumber) errors.push(t('textErorLicenseNumber1'));
        if (!data?.idCard) errors.push(t('textErorNationalID1'));
        if (!attachments.driverPhoto) errors.push(t('uploadProfilePhoto'));
        if (!attachments.idCardFront) errors.push(`${t('textUpload1')} ${t('cinCard1').toLowerCase()}!`);
        if (!attachments.idCardBack) errors.push(`${t('textUpload1')} ${t('cinCard2').toLowerCase()}!`);
        if (!attachments.driverLicence) errors.push(`${t('textUpload1')} ${t('driverLicense').toLowerCase()}!`);
        if (!attachments.grayCardFront) errors.push(`${t('textUpload1')} ${t('grayCard1').toLowerCase()}!`);
        if (!attachments.grayCardBack) errors.push(`${t('textUpload1')} ${t('grayCard2').toLowerCase()}!`);
        if (!attachments.professionalCard) errors.push(`${t('textUpload1')} ${t('professionalCard').toLowerCase()}!`);
        if (!attachments.insurance) errors.push(`${t('textUpload1')} ${t('insurance').toLowerCase()}!`);
        if (!agreedToTerms) errors.push(t('termsCheckboxEror'));
        if (!recaptchaState) errors.push(t('recaptchaError'));
        if (data?.usedReferralCode && !/^\d{4}$/.test(data.usedReferralCode))
            errors.push(t('inputErorSizeReferralCode'));

        return errors;
    };

    const processDocumentCapture = async (
        capture: any,
        type: DocumentTypeEnums
    ): Promise<ProcessedDocument | undefined> => {
        try {
            const { data, status, bypassVerification } = await processIDCardMutation(capture);

            if (!data || !status) {
                throw new Error('Invalid response');
            }

            if (bypassVerification === true && status === 200) {
                return {
                    bypassVerification: true,
                    fullName: '',
                    documentNumber: '',
                    personalNumber: '',
                    birthDate: '',
                };
            }

            const result = extractIdCardData(JSON.parse(data));

            if (!result) {
                throw new Error('Invalid document');
            }
            return {
                bypassVerification,
                fullName: result.fullName,
                documentNumber: result.documentNumber,
                personalNumber: result.personalNumber,
                birthDate: result.birthDate,
            };
        } catch (error) {
            notification.error({
                message: t('identityVerificationError'),
                description: t('identityVerificationFailed'),
            });
        }
    };

    const handleCheckIdentity = async (updatedDriverData) => {
        const idCardImage = attachments['idCardFront'];
        const driverLicenceImage = attachments['driverLicence'];

        const [idCardResult, driverLicenceResult] = await Promise.all([
            processDocumentCapture(idCardImage, DocumentTypeEnums.ID_CARD),
            processDocumentCapture(driverLicenceImage, DocumentTypeEnums.DRIVING_LICENSE),
        ]);

        if (!idCardResult || !driverLicenceResult) {
            notification.error({
                message: t('identityVerificationError'),
                description: t('textInvalidIdentityDocuments'),
            });
            return false;
        }

        const {
            bypassVerification: idCardBypassVerification,
            fullName: idCardFullName,
            documentNumber: idCardDocumentNumber,
            birthDate: idCardBirthDate,
        } = idCardResult;

        const {
            bypassVerification: driverLicenceBypassVerification,
            fullName: driverLicenceFullName,
            documentNumber: driverLicenceDocumentNumber,
            birthDate: driverLicenceBirthDate,
            personalNumber: driverLicencePersonalNumber,
        } = driverLicenceResult;

        if (!idCardBypassVerification && !driverLicenceBypassVerification) {
            const standardLicenseNumber = updatedDriverData?.licenseNumber?.replace(/\//g, '');
            const isDifferent = !dayjs(updatedDriverData?.birthday).isSame(dayjs(driverLicenceBirthDate), 'day');

            if (isDifferent) {
                notification.error({
                    message: t('validationError'),
                    description: t('textInvalidIdentityDocuments'),
                });
                return false;
            }

            if (idCardDocumentNumber !== updatedDriverData?.idCard) {
                notification.error({
                    message: t('validationError'),
                    description: t('textInvalidNationalID'),
                });
                return false;
            }

            if (driverLicenceDocumentNumber !== standardLicenseNumber) {
                notification.error({
                    message: t('validationError'),
                    description: t('textInvalidDriverLicense'),
                });
                return false;
            }
        }
        return true;
    };

    const handleNext = async () => {
        if (window.fbq) {
            window.fbq('trackCustom', 'DriverFormStepNext', {
                step: currentStep,
            });
        }
        const values = await form.validateFields();
        const cleanedValues = Object.fromEntries(Object.entries(values).filter(([key]) => !(key in attachments)));
        setDriverData((prev) => ({ ...prev, ...cleanedValues }));

        try {
            if (!agreedToTerms) {
                notification.error({
                    message: t('validationError'),
                    description: t('termsCheckboxEror'),
                });
                return;
            }

            if (currentStep === steps.length - 1) {
                const files = attachmentOrder
                    .map((key) => attachments[key])
                    .flatMap((attachment) => (attachment ? [attachment] : []));
                const errors = validateFormData(driverData, attachments);

                if (errors.length > 0) {
                    notification.error({
                        message: t('validationError'),
                        description: (
                            <>
                                {Object.values(errors).map((err, index) => (
                                    <div key={index}>{err}</div>
                                ))}
                            </>
                        ),
                    });
                    return;
                }
                signUpDriverMutation({ ...driverData, signUpType: 'web' }, files)
                    .then((result) => {
                        if (result?.code === 'ALREADY_EXISTS') {
                            notification.error({
                                message: t('popupAlreadyExistsError'),
                                description: t('textAlreadyExistsError'),
                            });
                            return;
                        }

                        // TRACKING META
                        if (window.fbq) {
                            window.fbq('track', 'CompleteRegistration');
                        }
                        notification.success({
                            message: t('popupSuccess'),
                            description: t('textSuccess'),
                        });
                        navigate('/welcome');
                    })
                    .catch((err) => {
                        notification.error({
                            message: t('popupError'),
                            description: t('textError'),
                        });
                    });
            } else {
                const isBeforeMovingFromStep2 = currentStep === 1;
                if (isBeforeMovingFromStep2) {
                    // Step 2 → check identity BEFORE moving forward
                    const isValid = await handleCheckIdentity({ ...driverData, ...cleanedValues });
                    if (!isValid) return; // stop if failed
                }
                setCurrentStep(currentStep + 1);
            }
        } catch (error) {
            notification.error({
                message: t('validationError'),
                description: t('textErorForm'),
            });
        }
    };

    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };

    const contentStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '0 16px',
    };

    const formStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: '500px',
        margin: '0 auto',
    };

    const dateYear = (current) => {
        const currentYear = new Date().getFullYear();
        return current && current.year() > currentYear;
    };

    const dateBirthYear = (current) => {
        const currentDate = new Date();
        const cutoffDate = new Date(currentDate.getFullYear() - 17, currentDate.getMonth(), currentDate.getDate());
        return current && current.isAfter(cutoffDate, 'day');
    };

    const startMonth = dayjs().subtract(17, 'year').startOf('year');

    const validateFullName = (_, value) => {
        if (!value || /^[a-zA-Z\s]+$/.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error(t('textErorFullname1')));
    };

    const validatePhoneNumber = (_, value) => {
        if (value && typeof value === 'string') {
            return Promise.resolve();
        }

        if (typeof value?.valid === 'function' && value?.valid() === true) {
            return Promise.resolve();
        }

        if (typeof value === 'object' && value?.valid() === false) {
            return Promise.reject(new Error(t('textErorPhone1')));
        }

        return Promise.reject(new Error(t('textErorPhone1')));
    };

    const validateNationalID = (_, value) => {
        if (!value || /^\d{8}$/.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error(t('textErorNationalID1')));
    };

    const validateLicenseNumber = (_, value) => {
        if (!value || /^\d{2}\/\d{6}$/.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error(t('textErorLicenseNumber1')));
    };

    const validateVehicleModel = (_, value) => {
        if (!value || /^[a-zA-Z0-9\s]+$/.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error(t('textErorModelVehicle1')));
    };

    return (
        <div style={contentStyle}>
            <Spin spinning={signUpDriverLoading || processIDCardLoading} size="large">
                <div style={{ color: 'black', fontWeight: 'semi-bold', fontSize: '25px' }}>
                    {steps[currentStep].content}
                </div>
                <div style={{ color: 'grey', marginTop: '16px' }}>{steps[currentStep].description1}</div>
                <div style={{ color: 'grey', marginTop: '8px', marginBottom: '8px' }}>
                    {steps[currentStep].description2}
                </div>
                <Steps current={currentStep} items={steps} responsive={false} style={{ marginBottom: '24px' }} />
                <Form form={form} layout="vertical" style={formStyle}>
                    {currentStep === 0 && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Row gutter={[16, 16]} style={{ width: '100%' }}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="fullName"
                                            label={t('fullName')}
                                            rules={[
                                                { required: true, message: t('textErorFullname2') },
                                                { validator: validateFullName },
                                            ]}
                                        >
                                            <Input
                                                placeholder={t('placeholderFullname')}
                                                onInput={(e) => {
                                                    const input = e.target as HTMLInputElement;
                                                    input.value = capitalizeWords(input.value);
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="birthday"
                                            label={t('birthday')}
                                            rules={[{ required: true, message: t('textErorBirthday') }]}
                                        >
                                            <DatePicker
                                                format={'DD-MM-YYYY'}
                                                disabledDate={dateBirthYear}
                                                defaultPickerValue={startMonth}
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="email"
                                            label={t('Email')}
                                            rules={[{ type: 'email', required: true, message: t('textErorEmail') }]}
                                        >
                                            <Input placeholder={t('placeholderEmail')} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="phoneNumber"
                                            label={t('phoneNumber')}
                                            rules={[
                                                { required: true, message: t('textErorPhone2') },
                                                { validator: validatePhoneNumber },
                                            ]}
                                        >
                                            <PhoneInput
                                                distinct
                                                enableArrow
                                                enableSearch
                                                disableParentheses
                                                excludeCountries={['il']}
                                                searchPlaceholder={t('placeholderPhone')}
                                                onChange={(value) => {
                                                    const formattedPhoneNumber = value.phoneNumber
                                                        ?.replace(/\D/g, '')
                                                        .replace(/(\d{3})(?=\d)/g, '$1 ')
                                                        .trim();
                                                    const formatted = `+${value.countryCode} ${value.areaCode} ${formattedPhoneNumber}`;

                                                    if (value.valid()) {
                                                        form.setFieldsValue({ phoneNumber: formatted });
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="marque"
                                            label={t('vehicleBrand')}
                                            rules={[{ required: true, message: t('textErorBrandVehicle') }]}
                                        >
                                            <Select
                                                placeholder={t('placeholderBrandVehicle')}
                                                optionFilterProp="children"
                                                showSearch
                                                allowClear
                                            >
                                                {(AllCarModels ?? []).map(({ value, label }) => (
                                                    <Select.Option key={value} value={value}>
                                                        {label}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="model"
                                            label={t('vehicleModel')}
                                            rules={[
                                                { required: true, message: t('textErorModelVehicle2') },
                                                { validator: validateVehicleModel },
                                            ]}
                                        >
                                            <Input
                                                placeholder={t('placeholderModelVehicle')}
                                                onInput={(e) => {
                                                    const input = e.target as HTMLInputElement;
                                                    input.value = capitalizeWords(input.value);
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="immatriculation"
                                            label={t('licensePlate')}
                                            rules={[
                                                { required: true, message: t('textErorLicencePlate1') },
                                                {
                                                    pattern: /^[0-9]{3} Tunis [0-9]{3,4}$/,
                                                    message: t('textErorLicencePlate2'),
                                                },
                                            ]}
                                        >
                                            <Input
                                                type="tel"
                                                placeholder="123 Tunis 4567"
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    // Insert "Tunis" after the first 3 digits if it's not already there
                                                    if (/^\d{3}(?! Tunis)/.test(value)) {
                                                        value = value.replace(/^(\d{3})(.*)/, '$1 Tunis$2');
                                                    }
                                                    // Ensure there is exactly one space after "Tunis"
                                                    value = value.replace(/Tunis\s*/, 'Tunis ');
                                                    // Limit to the correct format: 3 digits + "Tunis" + up to 4 digits
                                                    value = value.replace(/^(\d{3} Tunis \d{3,4}).*/, '$1');
                                                    form.setFieldsValue({ immatriculation: value });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="color"
                                            label={t('VehicleColor')}
                                            rules={[{ required: true, message: t('textErorColorVehicle') }]}
                                        >
                                            <Select
                                                placeholder={t('placeholderColorVehicle')}
                                                optionFilterProp="children"
                                                showSearch
                                                allowClear
                                            >
                                                {(allColorOptions ?? []).map(({ value, label, hex }) => (
                                                    <Select.Option key={value} value={value}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {/* 🎨 Color circle */}
                                                            <span
                                                                style={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: hex,
                                                                    display: 'inline-block',
                                                                    border: '1px solid #ccc',
                                                                }}
                                                            />
                                                            {/* Label */}
                                                            {t(label)}
                                                        </div>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="year"
                                            label={t('vehicleRegistrationYear')}
                                            rules={[{ required: true, message: t('textErorYearVehicle') }]}
                                            getValueProps={(value) => ({
                                                value: value ? dayjs().year(value) : null,
                                            })}
                                            normalize={(value) => value?.year()?.toString()}
                                        >
                                            <DatePicker
                                                picker="year"
                                                disabledDate={dateYear}
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="usedReferralCode"
                                            label={t('referralCode')}
                                            rules={[
                                                { required: false },
                                                {
                                                    pattern: /^\d{4,4}$/,
                                                    message: t('inputErorSizeReferralCode'),
                                                },
                                            ]}
                                        >
                                            <Input
                                                inputMode="tel"
                                                maxLength={4}
                                                style={{ width: '100%' }}
                                                placeholder={t('placeholderReferralCode')}
                                                onKeyPress={(e) => {
                                                    // Allow only digits
                                                    if (!/[0-9]/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                suffix={
                                                    <Tooltip title={t('textReferralCode')} placement="rightTop">
                                                        <InfoCircleOutlined
                                                            style={{ color: '#1677ff', fontSize: '20px' }}
                                                        />
                                                    </Tooltip>
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                        <Form.Item
                                            label={t('profilePhoto')}
                                            rules={[{ required: true, message: t('uploadProfilePhoto') }]}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                flexDirection: 'column',
                                                textAlign: 'center',
                                            }}
                                            labelCol={{ style: { textAlign: 'center' } }}
                                            name={'driverPhoto'}
                                            valuePropName={'driverPhoto'}
                                            getValueFromEvent={(event) => event?.file}
                                        >
                                            <Upload
                                                accept="image/png, image/jpeg"
                                                maxCount={1}
                                                beforeUpload={() => false}
                                                onChange={({ file }) => updateAttachment('driverPhoto', file)}
                                                showUploadList={false}
                                                listType="picture-card"
                                                openFileDialogOnClick={!attachments['driverPhoto']}
                                            >
                                                {attachments['driverPhoto']?.size > 0 ? (
                                                    <ImageUploadPreview
                                                        file={attachments['driverPhoto']}
                                                        onDelete={() => handleDelete('driverPhoto')}
                                                    />
                                                ) : (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <PlusOutlined />
                                                        <div style={{ marginTop: 8 }}>{t('uploadImage')}</div>
                                                    </div>
                                                )}
                                            </Upload>
                                        </Form.Item>
                                    </Col>
                                    <CheckBoxWrapper
                                        style={{ display: 'flex', alignItems: 'flex-start', paddingBottom: '20px' }}
                                    >
                                        <Checkbox
                                            checked={agreedToTerms}
                                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <span style={{ textAlign: 'justify' }}>
                                            {t('termsCheckboxText1')}
                                            <Typography.Link color="#003EFF" href={Urls.termsOfUse} target="_blank">
                                                {t('Terms of Service')}
                                            </Typography.Link>
                                            {t('termsCheckboxText2')}
                                            <Typography.Link color="#003EFF" href={Urls.privacyPolicy} target="_blank">
                                                {t('Privacy Policy')}
                                            </Typography.Link>
                                            {t('termsCheckboxText3')}
                                        </span>
                                    </CheckBoxWrapper>
                                </Row>
                            </div>
                        </>
                    )}

                    {currentStep === 1 && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Row gutter={[16, 16]} style={{ width: '100%' }}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="idCard"
                                            label={t('nationalID')}
                                            rules={[
                                                { required: true, message: t('textErorNationalID2') },
                                                { validator: validateNationalID },
                                            ]}
                                        >
                                            <Input
                                                placeholder={t('placeholderNationalID')}
                                                maxLength={8}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    form.setFieldsValue({ idCard: value });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="licenseNumber"
                                            label={t('licenseNumber')}
                                            rules={[
                                                { required: true, message: t('textErorLicenseNumber2') },
                                                { validator: validateLicenseNumber },
                                            ]}
                                        >
                                            <Input
                                                type="tel"
                                                placeholder={t('placeholderLicenseNumber')}
                                                maxLength={9}
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    // Allow only numeric characters
                                                    value = value.replace(/\D/g, '');
                                                    if (/^\d{2}(?!\/)/.test(value)) {
                                                        value = value.replace(/^(\d{2})/, '$1/');
                                                    }
                                                    // Limit the length to exactly 2 digits + '/' + 6 digits
                                                    if (/^\d{2}\/\d*/.test(value)) {
                                                        value = value.slice(0, 9); // Max length: 2 digits + '/' + 6 digits
                                                    }
                                                    form.setFieldsValue({ licenseNumber: value });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Row gutter={[16, 16]} style={{ width: '100%' }}>
                                    {attachmentPersonalRequested.map((item, index) => (
                                        <Col span={12} key={index}>
                                            <Form.Item
                                                labelCol={{ style: { textAlign: 'center' } }}
                                                label={item.label}
                                                name={item.name}
                                                valuePropName={item.name}
                                                getValueFromEvent={(event) => event?.file}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: `${t('textUpload1')} ${item.label.toLowerCase()}${t('textUpload2')}`,
                                                    },
                                                ]}
                                            >
                                                <Upload
                                                    accept="image/png, image/jpeg"
                                                    maxCount={1}
                                                    beforeUpload={() => false}
                                                    onChange={({ file }) => updateAttachment(item.name, file)}
                                                    showUploadList={false}
                                                    listType="picture-card"
                                                    openFileDialogOnClick={!attachments[item.name]}
                                                >
                                                    {attachments[item.name]?.size > 0 ? (
                                                        <ImageUploadPreview
                                                            file={attachments[item.name]}
                                                            onDelete={() => handleDelete(item.name)}
                                                        />
                                                    ) : (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <PlusOutlined />
                                                            <div style={{ marginTop: 8 }}>{t('uploadImage')}</div>
                                                        </div>
                                                    )}
                                                </Upload>
                                            </Form.Item>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </>
                    )}

                    {currentStep === 2 && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Row gutter={[16, 16]} style={{ width: '100%' }}>
                                    {attachmentProfessionalRequested.map((item, index) => (
                                        <Col span={12} key={index}>
                                            <Form.Item
                                                labelCol={{ style: { textAlign: 'center' } }}
                                                label={item.label}
                                                name={item.name}
                                                valuePropName={item.name}
                                                getValueFromEvent={(event) => event?.file}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: `${t('textUpload1')} ${item.label.toLowerCase()}${t('textUpload2')}`,
                                                    },
                                                ]}
                                            >
                                                <Upload
                                                    accept="image/png, image/jpeg"
                                                    maxCount={1}
                                                    beforeUpload={() => false}
                                                    onChange={({ file }) => updateAttachment(item.name, file)}
                                                    showUploadList={false}
                                                    listType="picture-card"
                                                >
                                                    {attachments[item.name]?.size > 0 ? (
                                                        <ImageUploadPreview
                                                            file={attachments[item.name]}
                                                            onDelete={() => handleDelete(item.name)}
                                                        />
                                                    ) : (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <PlusOutlined />
                                                            <div style={{ marginTop: 8 }}>{t('uploadImage')}</div>
                                                        </div>
                                                    )}
                                                </Upload>
                                            </Form.Item>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                            <RecaptchaWrapper
                                style={{ display: 'flex', justifyContent: 'center', paddingBottom: '20px' }}
                            >
                                <ReCAPTCHA
                                    onChange={() => setRecaptchaState(true)}
                                    onExpired={() => setRecaptchaState(false)}
                                    onErrored={() => setRecaptchaState(false)}
                                    sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                                />
                            </RecaptchaWrapper>
                        </>
                    )}

                    <Form.Item>
                        <ButtonWrapper>
                            <button
                                className="animate radius8 NextInput semiBold"
                                onClick={handleNext}
                                disabled={loading || (currentStep < 2 ? recaptchaState : !recaptchaState)}
                                style={
                                    (currentStep < 2 ? recaptchaState : !recaptchaState)
                                        ? { backgroundColor: 'lightgrey' }
                                        : {}
                                }
                            >
                                {currentStep === steps.length - 1 ? t('btnFinish') : t('btnNext')}
                            </button>
                        </ButtonWrapper>

                        {currentStep > 0 && (
                            <ButtonWrapper>
                                <button
                                    className="animate radius8 semiBold PreviousInput"
                                    onClick={handlePrev}
                                    disabled={signUpDriverLoading}
                                >
                                    {t('btnPrevious')}
                                </button>
                            </ButtonWrapper>
                        )}
                    </Form.Item>
                </Form>
            </Spin>
        </div>
    );
};

export default InscriptionTaxiDriver;
const RecaptchaWrapper = styled.div`
    @media (max-width: 460px) {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;
const CheckBoxWrapper = styled.div`
    @media (max-width: 460px) {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;
const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-right: 10px;

    & > button {
        margin-right: 10px;
    }

    @media (max-width: 460px) {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;
